import React from 'react';
import './Scorecard.css';

const INDEX_DESCRIPTIONS = {
  NDVI: "NDVI: Vegetation health (higher is greener)",
  NDMI: "NDMI: Moisture content in vegetation",
  EVI: "EVI: Enhanced vegetation index (less atmospheric effect)",
  SVHI: "SVHI: Composite index evaluating vegetation vigor across multiple bands"
};

const ScoreCard = ({
  chartData,
  selectedSeason,
  selectedYear,
  selectedIndices,
  screenSize,
  seasonalScores = {},
  sidebar = false // <-- new prop for sidebar mode
}) => {
  const getHealthColor = (index, category) => {
    const colorMaps = {
      NDVI: {
        'Excellent': '#1a9850',
        'Good': '#a6d96a',
        'Fair': '#fdae61',
        'Poor': '#fd8d3c',
        'Very Poor': '#d73027',
        'No Data': '#999999'
      },
      NDMI: {
        'High Moisture': '#3182bd',
        'Moderate Moisture': '#9ecae1',
        'Low Moisture': '#deebf7',
        'Dry': '#f7fbff',
        'No Data': '#999999'
      },
      EVI: {
        'Very Dense': '#225ea8',
        'Dense': '#41b6c4',
        'Moderate': '#a1dab4',
        'Sparse': '#ffffcc',
        'Very Sparse': '#feebe2',
        'No Data': '#999999'
      },
      SVHI: {
        'Excellent': '#006837',
        'Good': '#31a354',
        'Fair': '#78c679',
        'Poor': '#c2e699',
        'Very Poor': '#ffffcc',
        'No Data': '#999999'
      }

    };
    return colorMaps[index]?.[category] || '#999999';
  };

  const formatScore = (score) => {
    if (score === 0 || score === null || score === undefined) return '0.0';
    return typeof score === 'number' ? score.toFixed(1) : '0.0';
  };

  const renderIndexCard = (index) => {
    const scores = seasonalScores[index];
    if (!scores) {
      return (
        <div key={index} className="score-card-item no-data">
          <div className="score-header">
            <h4>
              {index}
              <span className="index-info" title={INDEX_DESCRIPTIONS[index]}> ⓘ</span>
            </h4>
            <span className="season-badge">{selectedSeason || 'No Season'}</span>
          </div>
          <div className="score-content">
            <div className="main-score">0.0</div>
            <div className="health-status no-data">No Data Available</div>
            <div className="score-details">
              <span>Readings: 0</span>
              <span>Range: 0.0 - 0.0</span>
            </div>
          </div>
        </div>
      );
    }
    const healthColor = getHealthColor(index, scores.health_category);
    return (
      <div key={index} className="score-card-item" style={{ borderLeftColor: healthColor }}>
        <div className="score-header">
          <h4>
            {index}
            <span className="index-info" title={INDEX_DESCRIPTIONS[index]}> ⓘ</span>
          </h4>
          <span className="season-badge">{scores.season || selectedSeason}</span>
        </div>
        <div className="score-content">
          <div className="main-score" style={{ color: healthColor }}>
            {formatScore(scores.average_score)}
            <span className="score-unit">/100</span>
          </div>
          <div
            className="health-status"
            style={{
              backgroundColor: healthColor + '20',
              color: healthColor,
              border: `1px solid ${healthColor}40`
            }}
          >
            {scores.health_category}
          </div>
          <div className="score-details">
            <span>R: {scores.total_readings}</span>
            <span>{formatScore(scores.min_value)} - {formatScore(scores.max_value)}</span>
          </div>
        </div>
      </div>
    );
  };

  const getOverallHealthStatus = () => {
    const ndviScore = seasonalScores.NDVI?.average_score || 0;
    const eviScore = seasonalScores.EVI?.average_score || 0;
    const ndmiScore = seasonalScores.NDMI?.average_score || 0;
    const svhiScore = seasonalScores.SVHI?.average_score || 0;
    const avgScore = (ndviScore + eviScore + ndmiScore + svhiScore) / 4;
    if (avgScore >= 50) return { status: 'Excellent', color: '#1a9850' };
    if (avgScore >= 30) return { status: 'Good', color: '#a6d96a' };
    if (avgScore >= 20) return { status: 'Fair', color: '#fdae61' };
    if (avgScore >= 10) return { status: 'Poor', color: '#fd8d3c' };
    return { status: 'Very Poor', color: '#d73027' };
  };

  const overallHealth = getOverallHealthStatus();

  // Sidebar/vertical mode
  const containerClass = sidebar
    ? "score-card-container sidebar"
    : screenSize.isMobile
      ? "score-card-container mobile"
      : "score-card-container";

  return (
    <div className={containerClass}>
      <div className="score-card-header" style={{ position: sidebar ? 'sticky' : undefined, top: 0, background: sidebar ? 'rgba(32,32,32,0.85)' : undefined, zIndex: 2 }}>
        <h3>
          Seasonal Index Averages
          {selectedSeason && (
            <span style={{ fontSize: '1rem', marginLeft: 8, opacity: 0.8 }}>
              ({selectedSeason} {selectedYear || ''})
            </span>
          )}
        </h3>
        <div
          className="overall-health"
          style={{
            backgroundColor: overallHealth.color + '20',
            border: `2px solid ${overallHealth.color}`,
            color: overallHealth.color
          }}
        >
          Overall: {overallHealth.status}
        </div>
      </div>
      <div className="score-cards-grid">
        {selectedIndices.map(renderIndexCard)}
      </div>
      <div className="score-card-footer">
        <small>Averages from Sentinel-2 | {selectedSeason}</small>
      </div>
    </div>
  );
};

export default ScoreCard;